import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Modal from '../ui/Modal';
import QRCodeStyling from '../../lib/qr-code-styling';

import Logo from '../../assets/logo-qr-main.png';
import { getChatAvatarHash } from '../../global/helpers';
import { ApiMediaFormat, ApiUser } from '../../api/types';
import useMedia from '../../hooks/useMedia';
import { getActions, withGlobal } from '../../global';
import Button from '../ui/Button';
import { getQRFile } from '../../util/files';

type OwnProps = {
  isOpen: boolean;
  user?: ApiUser;
  link: string;
  text?: string;
  onClose: NoneToVoidFunction;
};

type StateProps = {
  lastSyncTime?: number;
};

const QR_SIZE = 263;

const qrCode = new QRCodeStyling({
  width: QR_SIZE,
  height: QR_SIZE + 50,
  margin: 30,
  type: 'svg',
  dotsOptions: {
    type: 'rounded',
    color: '#fff',
  },
  backgroundOptions: {
    gradient: {
      type: 'linear',
      rotation: 45,
      colorStops: [
        { offset: 0.24, color: '#3A85F3' },
        { offset: 0.75, color: '#1450A8' },
      ],
    },
  },
  cornersSquareOptions: {
    type: 'extra-rounded',
  },
  image: Logo,
  imageOptions: {
    imageSize: 0.3,
    margin: 3,
  },

  qrOptions: {
    errorCorrectionLevel: 'Q',
  },
});

const InviteQRCodeModal: FC<OwnProps & StateProps> = ({
  isOpen,
  user,
  link,
  text,
  lastSyncTime,
  onClose,
}) => {
  const { openChatWithShare } = getActions();
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const imageHash = getChatAvatarHash(user!, 'normal');
  const [isSharing, setIsSharing] = useState(false);

  const imgBlobUrl = useMedia(
    imageHash,
    false,
    ApiMediaFormat.BlobUrl,
    lastSyncTime
  );

  useEffect(() => {
    qrCode.append(qrCodeRef.current!);
  }, [isOpen]);

  useEffect(() => {
    qrCode.update({
      data: link,
      avatar: imgBlobUrl || '',
      text,
    });
  }, [link, imgBlobUrl, text]);

  const handleShare = useCallback(async () => {
    if (isSharing || !qrCode) return;
    setIsSharing(true);

    try {
      const file = await getQRFile(qrCode);
      if (file) {
        await openChatWithShare({ file });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSharing(false);
    }
  }, [qrCode, isSharing]);

  return (
    <Modal
      isOpen={isOpen}
      hasCloseButton
      onClose={onClose}
      header='Invite by QR Code'
      notScroll
      className='ChatOrUserPicker'
    >
      <div className='share-qr-container custom-scroll'>
        <div className='qr-inner'>
          <div className='qr-container' ref={qrCodeRef} />
        </div>
        <div className='description'>
          Anyone who has Ello installed will be able to connect with you by
          clicking on scanning the QR code.
        </div>
        <Button fullWidth isLoading={isSharing} onClick={handleShare}>
          Share QR Code
        </Button>
      </div>
    </Modal>
  );
};

export default memo(
  withGlobal((global): StateProps => {
    const { lastSyncTime } = global;

    return {
      lastSyncTime,
    };
  })(InviteQRCodeModal)
);
